import { Component, inject, OnInit, AfterViewInit, signal, viewChild, TemplateRef, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs';
import { TranslocoPipe, TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  ButtonComponent,
  TableComponent,
  BadgeComponent,
  ColumnConfig,
  ColumnType
} from 'htx-ui-library';
import { ProtectedContentComponent } from '../../../../organisms/protected-content/protected-content.component';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData
} from '../../../../organisms/confirmation-dialog/confirmation-dialog.component';
import { NavigationService } from '../../../../../services/navigation.service';
import { NotificationService } from '../../../../../services/form/notification.service';
import { DsaUserService } from '../../../../../services/dsa-users/dsa-user.service';
import { DsaUser } from '../../../../../services/dsa-users/dsa-user.model';

@Component({
  selector: 'app-dsa-users-page',
  imports: [
    CommonModule,
    TranslocoPipe,
    TranslocoDirective,
    ButtonComponent,
    TableComponent,
    BadgeComponent,
    ProtectedContentComponent
  ],
  templateUrl: './dsa-users-page.component.html',
  styleUrl: './dsa-users-page.component.scss'
})
export class DsaUsersPageComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private dsaUserService = inject(DsaUserService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);
  private navigationService = inject(NavigationService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  requiredRoles = this.navigationService.getAccessRoles('admin/users');

  users = signal<DsaUser[]>([]);
  loading = signal<boolean>(false);
  columnConfig = signal<ColumnConfig<DsaUser>[]>([]);

  statusTemplateRef = viewChild<TemplateRef<{ $implicit: DsaUser }>>('statusTemplate');
  actionTemplateRef = viewChild<TemplateRef<{ $implicit: DsaUser }>>('actionTemplate');

  ngOnInit(): void {
    this.load();
  }

  ngAfterViewInit(): void {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildColumns());
  }

  private buildColumns(): void {
    const t = (key: string): string => this.translocoService.translate(key);

    this.columnConfig.set([
      {
        name: 'name',
        columnType: ColumnType.PLAIN,
        headerLabel: t('dsaManagement.users.column.name'),
        cellContent: (u) => [u.firstName, u.lastName].filter(Boolean).join(' ')
      },
      {
        name: 'email',
        columnType: ColumnType.PLAIN,
        headerLabel: t('dsaManagement.users.column.email'),
        cellContent: (u) => u.email
      },
      {
        name: 'status',
        columnType: ColumnType.TEMPLATE,
        headerLabel: t('dsaManagement.users.column.status'),
        template: this.statusTemplateRef()
      },
      {
        name: 'action',
        columnType: ColumnType.TEMPLATE,
        headerLabel: t('dsaManagement.users.column.action'),
        template: this.actionTemplateRef()
      }
    ]);
  }

  load(): void {
    this.loading.set(true);
    this.dsaUserService.findAll().subscribe({
      next: (users) => {
        this.users.set(users);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.notificationService.showError('dsaManagement.users.loadError', 5000);
      }
    });
  }

  addUser(): void {
    void this.router.navigate(['admin', 'users', 'add']);
  }

  editUser(user: DsaUser): void {
    void this.router.navigate(['admin', 'users', user.iamId, 'edit']);
  }

  getTyped(row: unknown): DsaUser {
    return row as DsaUser;
  }

  toggleStatus(user: DsaUser): void {
    const iamId = user.iamId;
    if (!iamId) {
      return;
    }
    const deactivate = user.enabled !== false;
    const prefix = deactivate
      ? 'dsaManagement.users.deactivate'
      : 'dsaManagement.users.activate';
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ');

    const data: ConfirmationDialogData = {
      title: this.translocoService.translate(`${prefix}.confirmTitle`),
      message: this.translocoService.translate(`${prefix}.confirmMessage`, { name }),
      confirmText: this.translocoService.translate(`${prefix}.confirmButton`),
      cancelText: this.translocoService.translate('dsaManagement.users.cancel'),
      variant: deactivate ? 'danger' : 'success',
      iconVariant: deactivate ? 'danger' : 'success',
      icon: deactivate ? 'block' : 'check_circle'
    };

    this.dialog
      .open(ConfirmationDialogComponent, { data, width: '480px', autoFocus: false })
      .afterClosed()
      .pipe(filter((confirmed) => confirmed === true))
      .subscribe(() => {
        const request$ = deactivate
          ? this.dsaUserService.deactivate(iamId)
          : this.dsaUserService.activate(iamId);
        request$.subscribe({
          next: () => {
            this.notificationService.showSuccess(`${prefix}.success`, 5000);
            this.load();
          },
          error: () => this.notificationService.showError(`${prefix}.error`, 5000)
        });
      });
  }

  deleteUser(user: DsaUser): void {
    const iamId = user.iamId;
    if (!iamId) {
      return;
    }
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ');

    const data: ConfirmationDialogData = {
      title: this.translocoService.translate('dsaManagement.users.delete.confirmTitle'),
      message: this.translocoService.translate('dsaManagement.users.delete.confirmMessage', {
        name
      }),
      confirmText: this.translocoService.translate('dsaManagement.users.delete.confirmButton'),
      cancelText: this.translocoService.translate('dsaManagement.users.cancel'),
      variant: 'danger',
      iconVariant: 'danger',
      icon: 'delete'
    };

    this.dialog
      .open(ConfirmationDialogComponent, { data, width: '480px', autoFocus: false })
      .afterClosed()
      .pipe(filter((confirmed) => confirmed === true))
      .subscribe(() => {
        this.dsaUserService.delete(iamId).subscribe({
          next: () => {
            this.notificationService.showSuccess('dsaManagement.users.delete.success', 5000);
            this.load();
          },
          error: () => this.notificationService.showError('dsaManagement.users.delete.error', 5000)
        });
      });
  }
}
